import { useEffect, useState, useMemo } from 'react';
import styled from 'styled-components';
import { fetchUserAttributes } from 'aws-amplify/auth';
import { useNavigate } from 'react-router-dom';

import ContentWrapper from '../../components/ContentWrapper';
import { useSettings } from '../../context/settingsContext';
import { useAssignments } from '../../context/assignmentsContext';
import TabContent from '../../components/TabContent';
import Icon from '../../components/Icon';
import GridTable from '../../components/table/GridTable';
import { GridTableCell } from '../../components/table/GridTableCell';
import IconButton from '../../components/IconButton';
import type {
  AnyPrioListing,
  AnyThingsToActOn,
} from '../../client/portal/types.gen';
import { partnersConfig } from '../../utils/partners';

type TableData = {
  amount: number | null;
  assignmentText: string;
  assignedTo: string;
  assignmentType: string;
  claimcaseKey: string;
  deadline: string;
  externalClaimcaseRef: string | null;
  incidentDay: string;
  insuredObject: string;
  overdueOn: string;
  partnerName: string;
  priority: number;
  requestedBy: string | null;
  paymentMethod: string | null;
};

type SharedColumn = {
  type: 'boolean' | 'number' | 'string';
  name: string;
  label: string | JSX.Element;
  isSortable?: boolean;
  width?: string;
};

/**
 * The text shown in the "Assignment" column. Exhaustive over the union: a new
 * kind of thing to act on is a type error here until it gets a text.
 */
const assignmentTextOf = (thingToActOn: AnyThingsToActOn): string => {
  switch (thingToActOn.tag) {
    case 'AnyExpiredTask':
    case 'AnyUpcomingTask':
      return thingToActOn.taskText;
    case 'AnyUnansweredCoverQuestion':
      return thingToActOn.question;
    case 'AnyPaymentToSign':
    case 'AnyPaymentWasRejected':
    case 'AnyPaymentSanctionsHeld':
    case 'AnyNoReserveSet':
    case 'AnyUnassignedClaimcase':
      return '-';
  }
};

const assignmentToTableData = (assignment: AnyPrioListing): TableData => {
  const thingToActOn = assignment.thingToActOn;
  return {
    assignmentText: assignmentTextOf(thingToActOn),
    assignedTo: assignment.assignedTo ?? '-',
    assignmentType: thingToActOn.tag.replace('Any', ''),
    claimcaseKey: assignment.claimcaseKey,
    deadline:
      (thingToActOn.tag === 'AnyExpiredTask' ||
        thingToActOn.tag === 'AnyUpcomingTask') &&
      thingToActOn.mDeadline
        ? thingToActOn.mDeadline
        : '-',
    overdueOn:
      thingToActOn.tag === 'AnyExpiredTask' && thingToActOn.mDeadline
        ? thingToActOn.mDeadline
        : '-',
    externalClaimcaseRef: assignment.externalClaimcaseRef ?? null,
    incidentDay: assignment.incidentDay,
    insuredObject: assignment.insuredObject,
    partnerName: assignment.partnerName,
    priority: thingToActOn.priority,
    requestedBy:
      thingToActOn.tag === 'AnyPaymentToSign' ||
      thingToActOn.tag === 'AnyPaymentWasRejected' ||
      thingToActOn.tag === 'AnyPaymentSanctionsHeld'
        ? thingToActOn.requestedBy
        : null,
    paymentMethod:
      thingToActOn.tag === 'AnyPaymentToSign' ||
      thingToActOn.tag === 'AnyPaymentWasRejected' ||
      thingToActOn.tag === 'AnyPaymentSanctionsHeld'
        ? thingToActOn.paymentMethod.tag
        : null,
    amount:
      thingToActOn.tag === 'AnyPaymentToSign' ||
      thingToActOn.tag === 'AnyPaymentWasRejected' ||
      thingToActOn.tag === 'AnyPaymentSanctionsHeld'
        ? thingToActOn.amount
        : null,
  };
};

const TableRow = ({
  content,
  onClick,
}: {
  content: TableData;
  onClick?: (claimcaseKey: string, partnerName: string) => void;
}) => {
  const [isMouseOver, setIsMouseOver] = useState<boolean>(false);

  const handleMouseEnter = () => setIsMouseOver(true);
  const handleMouseLeave = () => setIsMouseOver(false);
  const handleClick = () =>
    onClick && onClick(content.claimcaseKey, content.partnerName);

  const formatPaymentMethod = (value: string) => value.replace('Payment', '');

  return (
    <>
      <TableCell
        isHovering={isMouseOver}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <PartnerCell>
          <PartnerLogo
            src={
              (partnersConfig[content.partnerName] || partnersConfig.default)
                .logo
            }
            alt=''
          />
          {partnersConfig[content.partnerName]?.name || content.partnerName}
        </PartnerCell>
      </TableCell>
      <TableCell
        isHovering={isMouseOver}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {content.claimcaseKey}
      </TableCell>
      <TableCell
        isHovering={isMouseOver}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {content.incidentDay}
      </TableCell>
      <TableCell
        isHovering={isMouseOver}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {content.insuredObject}
      </TableCell>
      <TableCell
        isHovering={isMouseOver}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {content.assignedTo}
      </TableCell>
      <TableCell
        isHovering={isMouseOver}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {content.assignmentType &&
          content.assignmentType.replace(/([A-Z])/g, ' $1').trim()}
      </TableCell>
      <TableCell
        isHovering={isMouseOver}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        isAssignmentText={true}
      >
        {(content.assignmentType === 'ExpiredTask' ||
          content.assignmentType === 'UpcomingTask' ||
          content.assignmentType === 'UnansweredCoverQuestion') &&
          content.assignmentText}

        {(content.assignmentType === 'PaymentToSign' ||
          content.assignmentType === 'PaymentWasRejected' ||
          content.assignmentType === 'PaymentSanctionsHeld') && (
          <div>
            {content.paymentMethod &&
              formatPaymentMethod(content.paymentMethod)}{' '}
            payment of{' '}
            {content.amount && content.amount.toLocaleString('sv-SE')}{' '}
            <Span isHovering={isMouseOver}>
              requested by {content.requestedBy}
            </Span>
          </div>
        )}
      </TableCell>
      <TableCell
        isHovering={isMouseOver}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {content.externalClaimcaseRef ?? '-'}
      </TableCell>
      <TableCell
        isHovering={isMouseOver}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {content.overdueOn}
      </TableCell>
    </>
  );
};

const AssignmentsView = () => {
  const navigate = useNavigate();
  const { partners } = useSettings();
  const { assignments, partnerMeta, isLoading, refetch } = useAssignments();
  const [currentUser, setCurrentUser] = useState<string>('');
  const [selectedAdmin, setSelectedAdmin] = useState<string>('All');
  const [showAdminFilter, setShowAdminFilter] = useState<boolean>(false);
  const [sortOptions, setSortOptions] = useState<{
    column: string;
    direction: 'asc' | 'desc';
  }>({
    column: 'overdueOn',
    direction: 'asc',
  });

  useEffect(() => {
    localStorage.setItem(
      'activeView',
      JSON.stringify({ view: 'assignments', url: null }),
    );
  }, []);

  useEffect(() => {
    const getUser = async () => {
      try {
        const { email } = await fetchUserAttributes();
        setCurrentUser(email ?? '');
      } catch (error) {
        console.error('Could not fetch user attributes:', error);
      }
    };
    getUser();
  }, []);

  const tableData = useMemo(
    () => assignments.map(assignmentToTableData),
    [assignments],
  );

  // Get unique list of admins from the data
  const knownAdmins = useMemo(() => {
    const admins = new Set<string>();
    tableData.forEach((item) => {
      if (item.assignedTo && item.assignedTo !== '-') {
        admins.add(item.assignedTo);
      }
    });
    return ['All', ...Array.from(admins).sort()];
  }, [tableData]);

  const failedPartners = useMemo(
    () => partnerMeta.filter((m) => m.status.tag !== 'PartnerFetchOk'),
    [partnerMeta],
  );

  const handleClaimCaseClick = (claimcaseKey: string, partnerName: string) => {
    const frontendBaseUrl =
      partners?.find((p) => p.name === partnerName)?.frontendBaseUrl ?? '';
    navigate(
      `/partner?url=${encodeURIComponent(frontendBaseUrl)}&redirect=${encodeURIComponent(`/claim-cases/${claimcaseKey}`)}`,
    );
  };

  const handleAdminSelect = (admin: string) => {
    setSelectedAdmin(admin);
    setShowAdminFilter(false);
  };

  const sharedColumns: SharedColumn[] = [
    {
      name: 'partnerName',
      label: 'Partner',
      type: 'string',
      isSortable: true,
    },
    {
      name: 'claimcaseKey',
      label: 'Claim case',
      type: 'string',
      isSortable: true,
    },
    {
      name: 'incidentDay',
      label: 'Date of accident',
      type: 'string',
      isSortable: true,
    },
    {
      name: 'insuredObject',
      label: 'Insured object',
      type: 'string',
      isSortable: true,
    },
    {
      name: 'assignedTo',
      label: 'Assigned to',
      type: 'string',
      isSortable: true,
    },
    {
      name: 'assignmentType',
      label: 'Type',
      type: 'string',
      isSortable: true,
    },
    {
      name: 'assignmentText',
      label: 'Assignment',
      type: 'string',
      isSortable: false,
      width: 'minmax(13.75rem, max-content)',
    },
    {
      name: 'externalClaimcaseRef',
      label: 'External claim case reference',
      type: 'string',
      isSortable: true,
    },
    {
      name: 'overdueOn',
      label: 'Overdue on',
      type: 'string',
      isSortable: true,
    },
  ];

  const myAssignments = tableData.filter(
    (assignment) => assignment.assignedTo === currentUser,
  );

  const allAssignments = tableData.filter(
    (assignment) =>
      selectedAdmin === 'All' || assignment.assignedTo === selectedAdmin,
  );

  return (
    <ContentWrapper>
      <Header>
        <h1>Assignments</h1>
      </Header>
      {failedPartners.length > 0 && (
        <PartnerWarning>
          Could not load assignments from:{' '}
          {failedPartners.map((m, i) => (
            <span key={m.partnerName}>
              {i > 0 && ', '}
              <strong>
                {partnersConfig[m.partnerName]?.name || m.partnerName}
              </strong>
              {m.status.tag === 'PartnerFetchTimedOut' && ' (timed out)'}
              {m.status.tag === 'PartnerFetchError' && ' (error)'}
            </span>
          ))}
        </PartnerWarning>
      )}
      <TabContent
        contents={[
          {
            item: {
              content: (
                <TabItemContent>
                  <Icon iconName='faUser' />
                  My assignments
                </TabItemContent>
              ),
            },
            component: (
              <>
                <ActionRow>
                  <RefreshButton onClick={refetch} disabled={isLoading}>
                    <Icon iconName='faArrowsRotate' /> Refresh
                  </RefreshButton>
                </ActionRow>
                {isLoading || myAssignments.length > 0 ? (
                  <GridTable
                    columnOptions={sharedColumns}
                    tableData={myAssignments}
                    Row={TableRow}
                    sortOptions={sortOptions}
                    setSortOption={setSortOptions}
                    tableRowCustomProps={{
                      onClick: handleClaimCaseClick,
                    }}
                    isLoading={isLoading}
                  />
                ) : (
                  <p>Currently you have no assignments.</p>
                )}
              </>
            ),
          },
          {
            item: {
              content: (
                <TabItemContent>
                  <Icon iconName='faUsers' />
                  All assignments
                </TabItemContent>
              ),
            },
            component: (
              <>
                <ActionRow>
                  <FilterRow>
                    <FilterLabel>Selected user:</FilterLabel>
                    <FilterValue>
                      {selectedAdmin}
                      <IconButton
                        options={{
                          size: 'medium',
                          backgroundColor: 'transparent',
                        }}
                        onClick={() => setShowAdminFilter(!showAdminFilter)}
                      >
                        <Icon iconName='faFilter' />
                      </IconButton>
                      {showAdminFilter && (
                        <FilterDropdown>
                          {knownAdmins.map((admin) => (
                            <FilterOption
                              key={admin}
                              onClick={() => handleAdminSelect(admin)}
                              isSelected={admin === selectedAdmin}
                            >
                              {admin}
                            </FilterOption>
                          ))}
                        </FilterDropdown>
                      )}
                    </FilterValue>
                  </FilterRow>
                  <RefreshButton onClick={refetch} disabled={isLoading}>
                    <Icon iconName='faArrowsRotate' /> Refresh
                  </RefreshButton>
                </ActionRow>
                {isLoading || allAssignments.length > 0 ? (
                  <GridTable
                    columnOptions={sharedColumns}
                    tableData={allAssignments}
                    Row={TableRow}
                    sortOptions={sortOptions}
                    setSortOption={setSortOptions}
                    tableRowCustomProps={{
                      onClick: handleClaimCaseClick,
                    }}
                    isLoading={isLoading}
                  />
                ) : (
                  <p>Currently there are no assignments.</p>
                )}
              </>
            ),
          },
        ]}
        size={'medium'}
      />
    </ContentWrapper>
  );
};

export default AssignmentsView;

const Header = styled.header`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const PartnerWarning = styled.div`
  background: #fff8f0;
  border: 1px solid #fc8e49;
  border-radius: 0.25rem;
  padding: 0.5rem 1rem;
  margin-bottom: 1rem;
  font-size: 0.9rem;
  color: #6b4f35;
`;

const TabItemContent = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const TableCell = styled(GridTableCell)<{
  isHovering?: boolean;
  isAssignmentText?: boolean;
}>`
  color: ${({ isHovering }) => (isHovering ? '#fc8e49' : 'inherit')};
  text-decoration: ${({ isHovering }) => (isHovering ? 'underline' : 'none')};
  cursor: pointer;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: ${({ isAssignmentText }) =>
    isAssignmentText ? 'wrap' : 'nowrap'};
`;

const Span = styled.span<{ isHovering: boolean }>`
  font-size: 0.8rem;
  font-weight: 500;
  color: ${({ isHovering }) => (isHovering ? '#fc8e49' : '#7b7d8c')};
  line-height: 1.1;
  font-style: italic;
`;

const ActionRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const RefreshButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.4rem 0.75rem;
  border: 1px solid #dedede;
  border-radius: 0.25rem;
  background: white;
  color: #333;
  font-size: 0.85rem;
  cursor: pointer;

  &:hover:not(:disabled) {
    border-color: #fc8e49;
    color: #fc8e49;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const FilterRow = styled.div`
  display: flex;
  flex-direction: column;
  width: fit-content;
`;

const FilterLabel = styled.span`
  font-size: 0.8rem;
  font-weight: 500;
  color: #7b7d8c;
  line-height: 1.1;
`;

const FilterValue = styled.div`
  display: flex;
  position: relative;
`;

const FilterDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  background: white;
  border: 1px solid #dedede;
  border-radius: 0.25rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  z-index: 100;
  max-height: 300px;
  overflow-y: auto;
  min-width: 200px;
`;

const FilterOption = styled.div<{ isSelected: boolean }>`
  padding: 0.5rem 1rem;
  cursor: pointer;
  background: ${({ isSelected }) => (isSelected ? '#fff2eb' : 'white')};
  color: ${({ isSelected }) => (isSelected ? '#fc8e49' : 'inherit')};

  &:hover {
    background: #f5f5f5;
  }
`;

const PartnerCell = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const PartnerLogo = styled.img`
  width: 1.25rem;
  height: 1.25rem;
  border-radius: 0.25rem;
  object-fit: contain;
  flex-shrink: 0;
`;
